import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { ActivationGroup } from '@/features/activationGroup/types'
import type { ExtendedBid } from '@/features/bidding/types/bid'
import type { ValidationError } from '@/features/bidding/utils/cascading/bidLadder/transformBidLevelsToExtendedBids'
import { wattHoursToMegawattHours } from '@/utils/powerEnergyTransformations'

type ReviewBidsTableProps = {
  bids: ExtendedBid[]
  errors: ValidationError[]
  activationGroupsMap: Map<string, ActivationGroup>
}

// Styling constants
const CELL_PADDING = '8px 12px'
const CELL_PADDING_COMPACT = '6px 12px'
const FONT_SIZE_BODY = '0.8125rem'
const FONT_SIZE_HEADER = '0.75rem'
const FONT_SIZE_SUBHEADER = '0.6875rem'

// Colors
const HEADER_BG = '#f8fafc'
const HEADER_BG_DARK = '#f1f5f9'
const ROW_STRIPE_BG = '#fafbfc'
const STICKY_SHADOW = '2px 0 4px -1px rgba(0, 0, 0, 0.08)'
const ERROR_BG = '#fef2f2'
const ERROR_BORDER = '#ef4444'

function ReviewBidsTable({ bids, errors, activationGroupsMap }: Readonly<ReviewBidsTableProps>) {
  const { t } = useTranslation()

  const errorGroupUuids = useMemo(() => new Set(errors.map((e) => e.groupUuid)), [errors])

  if (bids.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: 200,
          p: 4,
          backgroundColor: '#fafbfc',
        }}
      >
        <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {t('bidding.bidding_cascading.review_bids.no_bids')}
        </Typography>
      </Box>
    )
  }

  return (
    <TableContainer
      sx={{
        flexGrow: 1,
        overflow: 'auto',
        // Scroll shadow indicators
        background: `
          linear-gradient(90deg, white 30%, rgba(255,255,255,0)),
          linear-gradient(90deg, rgba(255,255,255,0), white 70%) 0 100%,
          radial-gradient(farthest-side at 0 50%, rgba(0,0,0,0.1), rgba(0,0,0,0)),
          radial-gradient(farthest-side at 100% 50%, rgba(0,0,0,0.1), rgba(0,0,0,0)) 0 100%
        `,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '40px 100%, 40px 100%, 14px 100%, 14px 100%',
        backgroundPosition: 'left center, right center, left center, right center',
        backgroundAttachment: 'local, local, scroll, scroll',
      }}
    >
      <Table
        stickyHeader
        size="small"
        sx={{
          borderCollapse: 'separate',
          borderSpacing: 0,
          minWidth: 'max-content',
        }}
      >
        <TableHead>
          {/* First header row: Hour + Activation Group names */}
          <TableRow>
            <TableCell
              component="th"
              rowSpan={2}
              scope="col"
              sx={{
                position: 'sticky',
                left: 0,
                zIndex: 4,
                backgroundColor: HEADER_BG_DARK,
                fontWeight: 600,
                fontSize: FONT_SIZE_HEADER,
                padding: CELL_PADDING,
                verticalAlign: 'bottom',
                borderRight: '1px solid',
                borderRightColor: 'divider',
                borderBottom: '1px solid',
                borderBottomColor: 'divider',
                boxShadow: STICKY_SHADOW,
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
                color: 'text.secondary',
                minWidth: 70,
              }}
            >
              {t('bidding.bidding_cascading.review_bids.hour')}
            </TableCell>
            {bids.map((bid, index) => {
              const activationGroup = activationGroupsMap.get(bid.activationGroupUuid)
              const groupName = activationGroup?.code ?? bid.activationGroupUuid
              const hasError = errorGroupUuids.has(bid.activationGroupUuid)
              const isLastGroup = index === bids.length - 1

              return (
                <TableCell
                  key={bid.activationGroupUuid}
                  colSpan={2}
                  component="th"
                  scope="colgroup"
                  sx={{
                    textAlign: 'center',
                    backgroundColor: hasError ? ERROR_BG : HEADER_BG,
                    borderBottom: 'none',
                    borderRight: isLastGroup ? 'none' : '1px solid',
                    borderRightColor: 'divider',
                    borderTop: hasError ? `3px solid ${ERROR_BORDER}` : 'none',
                    fontWeight: 600,
                    fontSize: FONT_SIZE_HEADER,
                    padding: CELL_PADDING,
                    color: hasError ? 'error.dark' : 'text.primary',
                    letterSpacing: '0.01em',
                  }}
                >
                  {groupName}
                </TableCell>
              )
            })}
          </TableRow>

          {/* Second header row: Volume and Price sub-columns */}
          <TableRow>
            {bids.map((bid, index) => {
              const hasError = errorGroupUuids.has(bid.activationGroupUuid)
              const isLastGroup = index === bids.length - 1

              return [
                <TableCell
                  key={`${bid.activationGroupUuid}-volume`}
                  component="th"
                  scope="col"
                  sx={{
                    textAlign: 'right',
                    backgroundColor: hasError ? ERROR_BG : HEADER_BG,
                    fontWeight: 500,
                    fontSize: FONT_SIZE_SUBHEADER,
                    color: 'text.secondary',
                    padding: CELL_PADDING_COMPACT,
                    borderBottom: '2px solid',
                    borderBottomColor: hasError ? ERROR_BORDER : 'primary.main',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('bidding.bidding_cascading.review_bids.volume')}
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.625rem',
                      color: 'text.disabled',
                      ml: 0.5,
                    }}
                  >
                    MW
                  </Typography>
                </TableCell>,
                <TableCell
                  key={`${bid.activationGroupUuid}-price`}
                  component="th"
                  scope="col"
                  sx={{
                    textAlign: 'right',
                    backgroundColor: hasError ? ERROR_BG : HEADER_BG,
                    fontWeight: 500,
                    fontSize: FONT_SIZE_SUBHEADER,
                    color: 'text.secondary',
                    padding: CELL_PADDING_COMPACT,
                    borderRight: isLastGroup ? 'none' : '1px solid',
                    borderRightColor: 'divider',
                    borderBottom: '2px solid',
                    borderBottomColor: hasError ? ERROR_BORDER : 'primary.main',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('bidding.bidding_cascading.review_bids.price')}
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.625rem',
                      color: 'text.disabled',
                      ml: 0.5,
                    }}
                  >
                    EUR
                  </Typography>
                </TableCell>,
              ]
            })}
          </TableRow>
        </TableHead>

        <TableBody>
          {Array.from({ length: 24 }, (_, hourIndex) => {
            const isEvenRow = hourIndex % 2 === 0
            const isLastRow = hourIndex === 23

            return (
              <TableRow
                key={hourIndex}
                sx={{
                  backgroundColor: isEvenRow ? 'transparent' : ROW_STRIPE_BG,
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                  '&:hover .hour-cell': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <TableCell
                  className="hour-cell"
                  component="th"
                  scope="row"
                  sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    backgroundColor: isEvenRow ? 'background.paper' : ROW_STRIPE_BG,
                    fontWeight: 500,
                    fontSize: FONT_SIZE_BODY,
                    fontVariantNumeric: 'tabular-nums',
                    padding: CELL_PADDING_COMPACT,
                    borderRight: '1px solid',
                    borderRightColor: 'divider',
                    borderBottom: isLastRow ? 'none' : '1px solid',
                    borderBottomColor: 'grey.100',
                    boxShadow: STICKY_SHADOW,
                    color: 'text.secondary',
                    transition: 'background-color 0.15s ease',
                  }}
                >
                  {`${hourIndex.toString().padStart(2, '0')}:00`}
                </TableCell>

                {bids.map((bid, index) => {
                  const ptu = bid.ptus[hourIndex]
                  const hasError = errorGroupUuids.has(bid.activationGroupUuid)
                  const isLastGroup = index === bids.length - 1

                  const cellBaseSx = {
                    fontSize: FONT_SIZE_BODY,
                    padding: CELL_PADDING_COMPACT,
                    fontVariantNumeric: 'tabular-nums',
                    borderBottom: isLastRow ? 'none' : '1px solid',
                    borderBottomColor: 'grey.100',
                    transition: 'background-color 0.15s ease',
                  }

                  const errorSx = hasError
                    ? {
                        backgroundColor: ERROR_BG,
                        color: 'error.dark',
                      }
                    : {}

                  if (!ptu) {
                    return [
                      <TableCell
                        key={`${bid.activationGroupUuid}-volume`}
                        sx={{
                          ...cellBaseSx,
                          ...errorSx,
                          textAlign: 'center',
                          color: 'text.disabled',
                        }}
                      >
                        —
                      </TableCell>,
                      <TableCell
                        key={`${bid.activationGroupUuid}-price`}
                        sx={{
                          ...cellBaseSx,
                          ...errorSx,
                          textAlign: 'center',
                          color: 'text.disabled',
                          borderRight: isLastGroup ? 'none' : '1px solid',
                          borderRightColor: 'divider',
                        }}
                      >
                        —
                      </TableCell>,
                    ]
                  }

                  const volumeMW = wattHoursToMegawattHours(ptu.offeredVolume.quantity)
                  const price = ptu.ptuChunks?.[0]?.offeredPrice ?? 0

                  return [
                    <TableCell
                      key={`${bid.activationGroupUuid}-volume`}
                      sx={{
                        ...cellBaseSx,
                        ...errorSx,
                        textAlign: 'right',
                        fontWeight: 400,
                      }}
                    >
                      {volumeMW.toFixed(2)}
                    </TableCell>,
                    <TableCell
                      key={`${bid.activationGroupUuid}-price`}
                      sx={{
                        ...cellBaseSx,
                        ...errorSx,
                        textAlign: 'right',
                        fontWeight: 500,
                        borderRight: isLastGroup ? 'none' : '1px solid',
                        borderRightColor: 'divider',
                      }}
                    >
                      {price.toFixed(2)}
                    </TableCell>,
                  ]
                })}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default ReviewBidsTable
